<script setup>
import { ref, computed, inject } from "vue";
import _take from "lodash/take";
import _find from "lodash/find";
import { useI18n } from "vue-i18n";
import { Dialog, DialogPanel, DialogTitle, Menu, MenuButton, MenuItem, MenuItems, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { TrashIcon as TrashIconOutline } from "@heroicons/vue/24/outline";
import { EllipsisVerticalIcon, PencilIcon, TrashIcon } from "@heroicons/vue/20/solid";
import { useForm } from "@/form";
import Badge from "@Components/Badge.vue";
import { ResourceTableBodyColumn, ResourceTableBodyRow } from "@Components/Table";
import EditLinkModal from "./EditLinkModal.vue";
import { FormDropdown, FormDropdownItem, FormDropdownTrigger } from "@Components/Form";

const props = defineProps({
    link: Object,
    postTypes: Array,
});
const emit = defineEmits(["deleted", "updated"]);

const $notify = inject("$notify");

const { t: __ } = useI18n();

const updateLinkModal = ref();
const destroyModalOpen = ref(false);
const showAllKeywords = ref(false);
const initialDisplay = 9;

const keywordsToDisplay = computed(() => {
    if (!showAllKeywords.value) {
        return _take(props.link.keywords, initialDisplay);
    }
    return props.link.keywords;
});

const destroy = () => {
    useForm({ id: props.link.id })
        .post("linking_manual_destroy")
        .then(() => {
            destroyModalOpen.value = false;

            $notify({
                title: __("Le lien #{id} a été supprimé.", { id: props.link.id }),
                type: "success",
            });

            emit("deleted");
        })
        .catch(() => {
            destroyModalOpen.value = false;

            $notify({
                title: __("Une erreur s'est produite lors de la suppression."),
                type: "error",
            });
        });
};
</script>

<template>
    <ResourceTableBodyRow>
        <ResourceTableBodyColumn class="max-w-sm pl-4 lg:pl-8 sm:pl-6">
            <div class="flex flex-col gap-y-1">
                <div v-if="link.post" class="text-sm font-medium leading-6 text-gray-900 truncate dark:text-white">
                    {{ link.post.post_title }} (#{{ link.post.ID }})
                </div>
                <div v-else class="text-sm font-medium leading-6 text-gray-900 truncate dark:text-white">{{ link.custom_url }}</div>
                <div class="mt-0.5 text-xs">#{{ link.id }}</div>
            </div>
        </ResourceTableBodyColumn>
        <ResourceTableBodyColumn>
            <div class="-m-0.5 flex flex-wrap items-center">
                <div class="p-0.5" v-for="keyword in keywordsToDisplay">
                    <Badge>{{ keyword }}</Badge>
                </div>
                <template v-if="link.keywords.length > initialDisplay">
                    <div class="p-0.5" v-if="!showAllKeywords">
                        <div class="text-xs cursor-pointer hover:underline" @click="showAllKeywords = true">{{ __("Tout afficher") }}</div>
                    </div>
                    <div class="p-0.5" v-else>
                        <div class="text-xs cursor-pointer hover:underline" @click="showAllKeywords = false">{{ __("Cacher") }}</div>
                    </div>
                </template>
            </div>
        </ResourceTableBodyColumn>
        <ResourceTableBodyColumn>
            <div class="-m-0.5 flex flex-wrap items-center">
                <div class="p-0.5" v-for="postType in link.post_types">
                    <Badge> {{ _find(postTypes, ["name", postType])?.label ?? postType }}</Badge>
                </div>
            </div>
        </ResourceTableBodyColumn>
        <ResourceTableBodyColumn>
            {{ link.max_links }}
        </ResourceTableBodyColumn>
        <ResourceTableBodyColumn>
            <div class="flex items-center space-x-1">
                <Badge :color="link.is_blank ? 'green' : 'gray'">{{ __("Blank") }}</Badge>
                <Badge :color="link.is_follow ? 'green' : 'gray'">{{ __("Follow") }}</Badge>
                <Badge :color="link.is_obfuscated ? 'green' : 'gray'">{{ __("Obfusquer") }}</Badge>
            </div>
        </ResourceTableBodyColumn>
        <ResourceTableBodyColumn class="pr-4 text-right lg:pr-8 sm:pr-6">
            <div class="flex items-center space-x-1">
                <FormDropdown position="left">
                    <template #trigger>
                        <FormDropdownTrigger
                            class="flex items-center text-gray-600 bg-white rounded-full dark:text-gray-400 dark:bg-gray-950 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-900">
                            <span class="sr-only">Open options</span>
                            <EllipsisVerticalIcon class="w-5 h-5" aria-hidden="true" />
                        </FormDropdownTrigger>
                    </template>
                    <FormDropdownItem :label="__('Modifier')" :icon="PencilIcon" @click="updateLinkModal.show()" />
                    <FormDropdownItem :label="__('Supprimer')" :icon="TrashIcon" @click="destroyModalOpen = true" />
                </FormDropdown>
            </div>

            <TransitionRoot as="template" :show="destroyModalOpen">
                <Dialog as="div" class="z-[100000] relative" @close="destroyModalOpen = false">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0">
                        <div class="bg-opacity-75 fixed inset-0 bg-gray-900 transition-opacity" />
                    </TransitionChild>

                    <div class="z-[100000] overflow-y-auto fixed inset-0">
                        <div class="min-h-full flex justify-center items-end p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild
                                as="template"
                                enter="ease-out duration-300"
                                enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enter-to="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leave-from="opacity-100 translate-y-0 sm:scale-100"
                                leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel
                                    class="transform overflow-hidden relative px-4 pt-5 pb-4 text-left bg-gray-800 rounded-lg border border-gray-700 shadow-xl transition-all sm:w-full sm:max-w-lg sm:p-6 sm:my-8">
                                    <div class="sm:flex sm:items-start">
                                        <div
                                            class="flex-shrink-0 mx-auto w-12 h-12 flex justify-center items-center bg-red-500 rounded-full sm:w-10 sm:h-10 sm:mx-0">
                                            <TrashIconOutline class="w-6 h-6 text-gray-200" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-white">{{
                                                __("Supprimer le lien #{id}", { id: link.id })
                                            }}</DialogTitle>
                                            <div class="mt-2">
                                                <p class="text-sm text-gray-400">
                                                    {{
                                                        __("Êtes-vous sûr de vouloir supprimer le lien #{id} ? Cette action ne peut pas être annulée.", {
                                                            id: link.id,
                                                        })
                                                    }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:flex sm:flex-row-reverse sm:mt-4">
                                        <button
                                            type="button"
                                            class="w-full inline-flex justify-center items-center px-3 py-2 text-sm font-semibold text-white bg-red-500 rounded-md shadow-sm focus-visible:outline-2 focus-visible:outline-red-500 focus-visible:outline focus-visible:outline-offset-2 hover:bg-red-400 sm:w-auto sm:ml-3"
                                            @click="destroy">
                                            {{ __("Supprimer") }}
                                        </button>
                                        <button
                                            type="button"
                                            class="w-full inline-flex justify-center items-center px-3 py-2 mt-3 text-sm font-semibold text-gray-400 bg-gray-800 rounded-md focus-visible:outline-2 focus-visible:outline-gray-700 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-200 hover:bg-gray-700 hover:shadow-sm sm:w-auto sm:mt-0"
                                            @click="destroyModalOpen = false">
                                            {{ __("Annuler") }}
                                        </button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
            <EditLinkModal ref="updateLinkModal" :postTypes="postTypes" :link="link" @updated="$emit('updated')" />
        </ResourceTableBodyColumn>
    </ResourceTableBodyRow>
</template>
